import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { startOfDay } from "date-fns";
import { PeriodType } from "../../../common/data/service-proxies";
import { useAverageIdlingTimeForTimeOfDay } from "../hooks/useIdleReportsHooks";
import { IdleReportsHookParams } from "../hooks/useIdleReportsHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import { formatTooltipAxisWithUnit } from "../fuel-economy-report/chartUtils";
import ReactECharts from "echarts-for-react";
import "../fuel-economy-report/FuelEconomyReport.css";

const buildDateLabels = (
  from: Date,
  to: Date,
  periodType: PeriodType,
): string[] => {
  const result: string[] = [];
  const fromStart = startOfDay(from);
  const toStart = startOfDay(to);
  const oneDay = 24 * 60 * 60 * 1000;

  if (periodType === PeriodType.Weekly) {
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(
        new Date(d).toLocaleDateString("en-US", { weekday: "short" }),
      );
    }
  } else {
    const days: Date[] = [];
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      days.push(new Date(d));
    }
    const byWeek: { [bucket: string]: number } = {};
    days.forEach((d) => {
      const dayOfMonth = d.getDate();
      const bucket = `${Math.floor((dayOfMonth - 1) / 7) + 1}`;
      byWeek[bucket] = 1;
    });
    Object.keys(byWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((b) => result.push(`W${b}`));
  }

  return result;
};

/** Backend returns time in seconds; convert to minutes for display. */
const secondsToMinutes = (values: number[] | null | undefined): number[] =>
  (values ?? []).map((v) => v / 60);

export const AverageTimeWidget: React.FC<IdleReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useAverageIdlingTimeForTimeOfDay({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const chartOption = useMemo(() => {
    if (!data) return null;
    const labels = buildDateLabels(
      fromDate,
      toDate,
      periodType ?? PeriodType.Weekly,
    );
    const morningValues = secondsToMinutes(data.morningValues);
    const noonValues = secondsToMinutes(data.noonValues);
    const nightValues = secondsToMinutes(data.nightValues);

    const hasData =
      morningValues.length > 0 ||
      noonValues.length > 0 ||
      nightValues.length > 0;
    if (!hasData) return null;

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: unknown) =>
          formatTooltipAxisWithUnit(params, "min"),
      },
      legend: {
        data: [
          translate("fuelEconomyReport.morning", { _: "Morning" }),
          translate("fuelEconomyReport.noon", { _: "Noon" }),
          translate("fuelEconomyReport.night", { _: "Night" }),
        ],
        bottom: 0,
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          name: translate("fuelEconomyReport.morning", { _: "Morning" }),
          type: "line",
          data: morningValues,
          smooth: true,
          itemStyle: { color: "#3b82f6" },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("fuelEconomyReport.noon", { _: "Noon" }),
          type: "line",
          data: noonValues,
          smooth: true,
          itemStyle: { color: "#ef4444" },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("fuelEconomyReport.night", { _: "Night" }),
          type: "line",
          data: nightValues,
          smooth: true,
          itemStyle: { color: "#22c55e" },
          symbol: "circle",
          symbolSize: 6,
        },
      ],
    };
  }, [
    data,
    fromDate,
    toDate,
    periodType,
    translate,
    theme.palette.text.primary,
  ]);

  const hasData = useMemo(() => {
    if (!data) return false;
    const morning = data.morningValues?.length ?? 0;
    const noon = data.noonValues?.length ?? 0;
    const night = data.nightValues?.length ?? 0;
    return morning > 0 || noon > 0 || night > 0;
  }, [data]);

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("idleReport.averageIdleTimeByTimeOfDay", {
            _: "Avg. idle time",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : chartOption ? (
          <ReactECharts style={{ height: 400, flex: 1 }} option={chartOption} />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AverageTimeWidget;
